import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { eventTypesAPI, bookingPagesAPI } from "../services/schedulerAPI";
import ConferenceProviderSelector from "../components/ConferenceProviderSelector";
import { showToast } from "../components/Toast";

const CreateGroupInvite = () => {
  const navigate = useNavigate();
  const [showPreview, setShowPreview] = useState(false);
  const [eventTypes, setEventTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    name: "",
    duration_minutes: 60,
    conference_provider: "zoom",
    description: "",
    slug: "",
    max_attendees: 10,
    show_multiple_durations: false,
    buffer_before: 0,
    buffer_after: 0,
  });

  useEffect(() => {
    loadEventTypes();
  }, []);

  const loadEventTypes = async () => {
    try {
      const data = await eventTypesAPI.list();
      setEventTypes(data);
    } catch (error) {
      console.error("Failed to load event types:", error);
      showToast.error("Failed to load existing event types");
    }
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  };

  const validateField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'name':
        if (!value || value.trim() === '') {
          newErrors.name = 'Name is required';
        } else if (value.length < 3) {
          newErrors.name = 'Name must be at least 3 characters';
        } else if (value.length > 100) {
          newErrors.name = 'Name must be less than 100 characters';
        } else {
          delete newErrors.name;
        }
        break;

      case 'duration_minutes':
        if (value < 5) {
          newErrors.duration_minutes = 'Duration must be at least 5 minutes';
        } else if (value > 480) {
          newErrors.duration_minutes = 'Duration cannot exceed 8 hours (480 minutes)';
        } else {
          delete newErrors.duration_minutes;
        }
        break;

      case 'max_attendees':
        if (value < 2) {
          newErrors.max_attendees = 'Group events must allow at least 2 attendees';
        } else if (value > 1000) {
          newErrors.max_attendees = 'Maximum attendees cannot exceed 1000';
        } else {
          delete newErrors.max_attendees;
        }
        break;

      case 'buffer_before':
      case 'buffer_after':
        if (value < 0) {
          newErrors[name] = 'Buffer time cannot be negative';
        } else if (value > 120) {
          newErrors[name] = 'Buffer time cannot exceed 120 minutes';
        } else {
          delete newErrors[name];
        }
        break;

      case 'slug':
        if (!value || value.trim() === '') {
          newErrors.slug = 'URL slug is required';
        } else if (!/^[a-z0-9-]+$/.test(value)) {
          newErrors.slug = 'Slug must only contain lowercase letters, numbers, and hyphens';
        } else {
          delete newErrors.slug;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    const slug = generateSlug(name);

    setFormData({
      ...formData,
      name,
      slug
    });

    validateField('name', name);
    validateField('slug', slug);
  };

  const handleFieldChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    validateField(field, value);
  };

  const validateAllFields = () => {
    const fields = ['name', 'duration_minutes', 'max_attendees', 'buffer_before', 'buffer_after', 'slug'];
    let isValid = true;

    fields.forEach(field => {
      if (!validateField(field, formData[field])) {
        isValid = false;
      }
    });

    return isValid;
  };

  const handleSave = async () => {
    // Validate all fields
    if (!validateAllFields()) {
      showToast.error("Please fix validation errors before saving");
      return;
    }

    setIsLoading(true);

    try {
      // First, check if we need to create an event type or use existing one
      let eventTypeId;

      const existingEventType = eventTypes.find(
        et => et.duration_minutes === formData.duration_minutes && et.name === formData.name
      );

      if (existingEventType) {
        eventTypeId = existingEventType.id;
        showToast.info("Using existing event type");
      } else {
        // Create new event type with conference provider
        showToast.info("Creating event type...");
        const newEventType = await eventTypesAPI.create({
          name: formData.name,
          description: formData.description,
          duration_minutes: formData.duration_minutes,
          buffer_before_minutes: formData.buffer_before,
          buffer_after_minutes: formData.buffer_after,
          conference_provider: formData.conference_provider,
          max_attendees_per_slot: formData.max_attendees,
          color: "#8B5CF6",
          is_active: true,
        });
        eventTypeId = newEventType.id;
        showToast.success("Event type created successfully!");
      }

      // Create booking page
      showToast.info("Creating booking page...");
      const bookingPageData = {
        slug: formData.slug,
        title: formData.name,
        description: formData.description,
        event_type_id: eventTypeId,
        custom_questions: [],
        redirect_url: "",
        is_active: true,
      };

      await bookingPagesAPI.create(bookingPageData);

      showToast.success("Group booking page created successfully!");

      // Navigate back to booking pages
      setTimeout(() => {
        navigate("/calendar/booking-pages");
      }, 1000);
    } catch (error) {
      console.error("Failed to create booking page:", error);
      const errorMessage = error.response?.data?.detail || error.message || "Failed to create invite";
      showToast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const durationOptions = [
    { value: 30, label: "30 min" },
    { value: 45, label: "45 min" },
    { value: 60, label: "60 min" },
    { value: 90, label: "90 min" },
    { value: 120, label: "2 hours" },
  ];

  const isFormValid = formData.name && formData.slug && Object.keys(errors).length === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="text-4xl font-bold text-gray-900">
              Create Group Invite
            </h1>
            <p className="text-gray-600 mt-2">Configure your group booking link for webinars, classes, and group sessions</p>
          </motion.div>
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="apple-btn-secondary flex items-center gap-2"
          >
            <span>👁</span>
            {showPreview ? "Hide Preview" : "Show Preview"}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form Section */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="apple-card p-8 space-y-6"
          >
            {/* Basic Info Section */}
            <div>
              <h2 className="text-sm font-bold text-gray-500 uppercase mb-4">
                BASIC INFO
              </h2>

              {/* Name Field */}
              <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <span className="text-red-500">*</span>
                  <span>Name</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={handleNameChange}
                  placeholder="Weekly Webinar"
                  className={`apple-input w-full ${errors.name ? 'border-red-500' : ''}`}
                  required
                />
                {errors.name && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <span>⚠️</span>
                    {errors.name}
                  </p>
                )}
              </div>

              {/* Event Duration */}
              <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <span>🕐</span>
                  <span>Event Duration</span>
                  <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.duration_minutes}
                  onChange={(e) => handleFieldChange('duration_minutes', parseInt(e.target.value))}
                  className="apple-input w-full"
                >
                  {durationOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                {errors.duration_minutes && (
                  <p className="text-xs text-red-500 mt-1">{errors.duration_minutes}</p>
                )}
              </div>

              {/* Max Attendees */}
              <div className="mb-6">
                <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                  <span>👥</span>
                  <span>Maximum Attendees</span>
                  <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="2"
                  max="1000"
                  value={formData.max_attendees}
                  onChange={(e) => handleFieldChange('max_attendees', parseInt(e.target.value) || 2)}
                  className={`apple-input w-full ${errors.max_attendees ? 'border-red-500' : ''}`}
                />
                {errors.max_attendees && (
                  <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                    <span>⚠️</span>
                    {errors.max_attendees}
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Number of people who can book the same time slot
                </p>
              </div>

              {/* Show multiple duration options */}
              <div className="flex items-center gap-3 mb-6">
                <input
                  type="checkbox"
                  id="multiple_durations"
                  checked={formData.show_multiple_durations}
                  onChange={(e) =>
                    setFormData({ ...formData, show_multiple_durations: e.target.checked })
                  }
                  className="w-4 h-4 text-primary-600 rounded"
                />
                <label htmlFor="multiple_durations" className="text-sm text-gray-700">
                  Show multiple duration options
                </label>
              </div>
            </div>

            {/* Smart Schedule Settings */}
            <div className="border-t border-gray-200 pt-6">
              <h2 className="text-sm font-bold text-gray-500 uppercase mb-4">
                SMART SCHEDULE SETTINGS
              </h2>

              {/* Conference Provider */}
              <div className="mb-6">
                <ConferenceProviderSelector
                  value={formData.conference_provider}
                  onChange={(provider) => handleFieldChange('conference_provider', provider)}
                  error={errors.conference_provider}
                />
              </div>

              {/* Buffer Times */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                  <span>⏱️</span>
                  <span>Buffer Times</span>
                  <span className="text-gray-500 font-normal text-xs ml-2">
                    (Add padding before/after meetings)
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-gray-600 mb-2">
                      Before (minutes)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="120"
                      value={formData.buffer_before}
                      onChange={(e) => handleFieldChange('buffer_before', parseInt(e.target.value) || 0)}
                      className={`apple-input w-full ${errors.buffer_before ? 'border-red-500' : ''}`}
                    />
                    {errors.buffer_before && (
                      <p className="text-xs text-red-500 mt-1">{errors.buffer_before}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm text-gray-600 mb-2">
                      After (minutes)
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="120"
                      value={formData.buffer_after}
                      onChange={(e) => handleFieldChange('buffer_after', parseInt(e.target.value) || 0)}
                      className={`apple-input w-full ${errors.buffer_after ? 'border-red-500' : ''}`}
                    />
                    {errors.buffer_after && (
                      <p className="text-xs text-red-500 mt-1">{errors.buffer_after}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Description */}
            <div className="border-t border-gray-200 pt-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Description (Optional)
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                {/* Simple toolbar */}
                <div className="bg-gray-50 border-b border-gray-300 p-2 flex gap-1">
                  <button className="p-2 hover:bg-gray-200 rounded" title="Bold">
                    <strong>B</strong>
                  </button>
                  <button className="p-2 hover:bg-gray-200 rounded" title="Italic">
                    <em>I</em>
                  </button>
                  <button className="p-2 hover:bg-gray-200 rounded" title="Underline">
                    <u>U</u>
                  </button>
                  <button className="p-2 hover:bg-gray-200 rounded" title="Link">
                    🔗
                  </button>
                </div>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Add a description for your group event..."
                  rows={4}
                  className="w-full px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            {/* Booking Link Preview */}
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <div className="text-xs text-gray-500 mb-1">Booking Link</div>
              <div className="flex items-center gap-2">
                <code className="text-sm text-gray-700 flex-1 truncate">
                  {window.location.origin}/book/{formData.slug || "your-group-event"}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`${window.location.origin}/book/${formData.slug}`);
                    showToast.success("Link copied to clipboard!");
                  }}
                  className="text-purple-600 hover:text-purple-700 text-sm"
                  disabled={!formData.slug}
                >
                  📋 Copy
                </button>
              </div>
            </div>
          </motion.div>

          {/* Preview Section */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            className="apple-card p-8 bg-white"
          >
            <div className="text-sm text-gray-500 mb-4">PREVIEW</div>

            <div className="border-2 border-gray-200 rounded-xl p-6 bg-gradient-to-br from-purple-50 to-white">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {formData.name || "Your Group Event"}
              </h3>

              <div className="flex items-center gap-2 text-gray-600 mb-4">
                <span>🕐</span>
                <span>{formData.duration_minutes} min</span>
              </div>

              <div className="flex items-center gap-2 text-gray-600 mb-2">
                <span>{formData.conference_provider === 'zoom' ? '🎥' : '📆'}</span>
                <span className="text-sm">
                  {formData.conference_provider === 'zoom' ? 'Zoom Meeting' : 'Google Meet'}
                </span>
              </div>

              <div className="flex items-center gap-2 text-purple-600 mb-4">
                <span>👥</span>
                <span className="text-sm font-semibold">
                  Up to {formData.max_attendees} attendees
                </span>
              </div>

              {(formData.buffer_before > 0 || formData.buffer_after > 0) && (
                <div className="flex items-center gap-2 text-gray-500 text-sm mb-4">
                  <span>⏱️</span>
                  <span>
                    {formData.buffer_before > 0 && `${formData.buffer_before}m before`}
                    {formData.buffer_before > 0 && formData.buffer_after > 0 && ' • '}
                    {formData.buffer_after > 0 && `${formData.buffer_after}m after`}
                  </span>
                </div>
              )}

              {formData.description && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">
                    {formData.description}
                  </p>
                </div>
              )}

              <div className="mt-6">
                <div className="text-sm font-semibold text-gray-700 mb-3">
                  Select a Date
                </div>
                <div className="grid grid-cols-7 gap-2">
                  {[...Array(14)].map((_, i) => (
                    <div
                      key={i}
                      className="aspect-square bg-gray-100 rounded-lg flex items-center justify-center text-sm text-gray-600"
                    >
                      {i + 1}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between mt-8">
          <button
            onClick={() => navigate("/calendar/select-link-type")}
            className="apple-btn-secondary"
            disabled={isLoading}
          >
            ← Back
          </button>
          <button
            onClick={handleSave}
            disabled={!isFormValid || isLoading}
            className="apple-btn-primary disabled:opacity-50 disabled:cursor-not-allowed px-8 flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>Creating...</span>
              </>
            ) : (
              <>
                <span>Create Group Booking Page</span>
                <span>→</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateGroupInvite;
